import { ref } from "vue";
import { useDonationFormStore } from "../../stores/useDonationFormStore";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * The campaigns a campaign picker must present as ALREADY USED. Both pickers (`CampaignSection`,
 * `FormCreateModal`) go through here so the two policies below cannot drift between them.
 *
 * CONSTRAINT: this feature must only ever UNDER-flag. Over-flagging greys a campaign that is
 * genuinely free, which the manager experiences as a refusal with no error anywhere to explain it;
 * under-flagging costs one round-trip and yields the service's localized 409. Two consequences a
 * future editor would plausibly "fix" the wrong way:
 *
 *  1. A TRUNCATED OR FAILED SWEEP FLAGS NOTHING. `fetchTakenCampaignIds` inspects a single page, so
 *     past that page its answer is a LOWER BOUND — a campaign absent from the set is not thereby
 *     free. Flagging on a partial view teaches the manager to trust a signal that is silently wrong
 *     for every row nobody fetched (R15); the 409 stays the authority, mapped to copy by R70.
 *  2. THE EDITED FORM'S OWN CAMPAIGN IS NEVER FLAGGED. Re-posting the campaign a form already
 *     carries is a 200 no-op (`SetCampaignHandler` gates the check on the campaign actually
 *     CHANGING), so flagging it would render the field's own current value as forbidden.
 */
export function useTakenCampaigns(options = {}) {
    const store = useDonationFormStore();
    const takenCampaignIds = ref(new Set());
    /**
     * CONSTRAINT: discriminates concurrent sweeps so only the LATEST may write. `FormCreateModal`
     * re-primes on every open, so two sweeps overlap whenever the pop-up is reopened before the first
     * resolves, and the answers are not guaranteed to arrive in order. Two sweeps seconds apart
     * barely differ, which is exactly what makes a stale write invisible (R97).
     */
    let sweepToken = 0;
    /** Never throws: a picker must stay usable when this lookup is the thing that failed. */
    async function load() {
        const token = ++sweepToken;
        try {
            const pool = await store.fetchTakenCampaignIds();
            if (token !== sweepToken)
                return; // a later sweep already answered
            takenCampaignIds.value = pool.truncated ? new Set() : pool.campaignIds;
        }
        catch (err) {
            if (token !== sweepToken)
                return;
            // R47: the only place this failure is visible. Degrading to "flag nothing" is the direction
            // that cannot silently forbid a legal choice.
            logger.error("[useTakenCampaigns] Failed to collect the taken campaigns", err);
            takenCampaignIds.value = new Set();
        }
    }
    function isTaken(campaignId) {
        if (campaignId === options.ownCampaignId?.())
            return false;
        return takenCampaignIds.value.has(campaignId);
    }
    return { load, isTaken };
}
